/**
 * Tree: replacement for bootstrap-treeview as used on the chart-of-accounts page.
 * Same markup (div.treeview > ul.list-group > li.list-group-item with span.indent / span.icon),
 * same options (data with `text` + `nodes`, levels, expandIcon, collapseIcon, emptyIcon)
 * and the same select semantics: clicking a node selects it and fires onNodeSelected;
 * clicking it again unselects it without firing.
 */
export class Tree {
    constructor(target, options = {}) {
        this.el = typeof target === 'string' ? document.querySelector(target) : target;
        this.options = {
            levels: 2,
            expandIcon: 'fas fa-angle-right',
            collapseIcon: 'fas fa-angle-down',
            emptyIcon: 'fas fa-circle',
            highlightSelected: true,
            onNodeSelected: null,
            ...options,
        };
        this.selected = null;
        this.nodes = [];
        this.flatten(this.options.data || [], 0, null);
        this.onClick = this.onClick.bind(this);
        this.el.addEventListener('click', this.onClick);
        this.render();
    }

    flatten(list, level, parent) {
        list.forEach((node) => {
            const entry = {
                id: this.nodes.length,
                node,
                level,
                parent,
                children: [],
                expanded: level + 1 < this.options.levels,
            };
            this.nodes.push(entry);
            if (parent) parent.children.push(entry);
            if (Array.isArray(node.nodes) && node.nodes.length) this.flatten(node.nodes, level + 1, entry);
        });
    }

    isVisible(entry) {
        for (let p = entry.parent; p; p = p.parent) if (!p.expanded) return false;
        return true;
    }

    render() {
        const { expandIcon, collapseIcon, emptyIcon, highlightSelected } = this.options;
        const items = this.nodes
            .filter((e) => this.isVisible(e))
            .map((e) => {
                const indent = '<span class="indent"></span>'.repeat(e.level);
                const icon = e.children.length
                    ? `<span class="icon expand-icon ${e.expanded ? collapseIcon : expandIcon}"></span>`
                    : `<span class="icon ${emptyIcon}"></span>`;
                const selected = highlightSelected && this.selected === e.id ? ' node-selected active' : '';
                return `<li class="list-group-item node-${this.el.id}${selected}" data-nodeid="${e.id}">${indent}${icon}<span class="icon node-icon"></span>${e.node.text ?? ''}</li>`;
            })
            .join('');
        this.el.classList.add('treeview');
        this.el.innerHTML = `<ul class="list-group">${items}</ul>`;
    }

    onClick(e) {
        const li = e.target.closest('li[data-nodeid]');
        if (!li || !this.el.contains(li)) return;
        const entry = this.nodes[Number(li.dataset.nodeid)];
        if (e.target.classList.contains('expand-icon')) {
            entry.expanded = !entry.expanded;
            this.render();
            return;
        }
        if (this.selected === entry.id) {
            this.selected = null;
        } else {
            this.selected = entry.id;
            if (this.options.onNodeSelected) this.options.onNodeSelected.call(this.el, e, entry.node);
        }
        this.render();
    }

    destroy() {
        this.el.removeEventListener('click', this.onClick);
        this.el.innerHTML = '';
    }
}
